import { CurrencyPipe, DatePipe, TitleCasePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { BankAccount, BankAccountHelper, BankAccountRole, BankAccountType, ButtonColor, CommonActionKey, EnumHelper, HorizontalListData, HorizontalListFactory, HorizontalListItemSize, SimpleDialogData } from "common";
import { ConfirmChangeActionType, ConfirmChangesDialogData } from "../../shared/confirm-changes-dialog/confirm-changes-dialog.model";
import { BankAccountRoleLabel } from "../bank.model";

@Injectable()
export class BankAccountDetailsAdapter {
  constructor(
    private readonly datePipe: DatePipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly titleCasePipe: TitleCasePipe
  ) { }

  adaptBankAccountsListData = (data: BankAccount): HorizontalListData[] => {
    const isProviderMsgItemVisible = !!data?.syncedBankAccount?.providerMsg?.trim();
    const isMissingInfoItemVisible = !!data?.missingInformation?.length;

    return [
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Link status',
        customTemplateName: 'linkStatusValue',
        hideDividerBelow: isProviderMsgItemVisible || isMissingInfoItemVisible,
      }),
      HorizontalListFactory.createInfoStringItem({
        displayedValue: data?.syncedBankAccount?.providerMsg,
        isItemHidden: !isProviderMsgItemVisible,
        hideDividerBelow: true,
        itemSize: HorizontalListItemSize.Small
      }),
      HorizontalListFactory.createCustomTemplateItem({
        customTemplateName: 'missingInfo',
        isItemHidden: !isMissingInfoItemVisible,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Created on',
        displayedValue: this.datePipe.transform(data?.createdOn) || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Created by',
        displayedValue: data?.createdBy || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Updated on',
        displayedValue: this.datePipe.transform(data?.updatedOn) || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Updated by',
        displayedValue: data?.updatedBy || '-',
      }),
    ]
  }

  adaptBankIntegrationListData = (data: BankAccount): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Balance',
        displayedValue: this.currencyPipe.transform(data?.syncedBankAccount?.balance, '$'),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Balance date',
        displayedValue: this.datePipe.transform(data?.syncedBankAccount?.balanceDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Type',
        displayedValue: data?.syncedBankAccount?.type,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Name',
        displayedValue: data?.syncedBankAccount?.name,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Bank name',
        displayedValue: data?.syncedBankAccount?.institutionName,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Account number',
        displayedValue: data?.syncedBankAccount?.accountNumber,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Routing number',
        displayedValue: data?.syncedBankAccount?.routingNumber,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Account owner',
        displayedValue: data?.syncedBankAccount?.ownerName || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Account owner address',
        displayedValue: data?.syncedBankAccount?.ownerAddress || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Status',
        displayedValue: this.titleCasePipe.transform(data?.syncedBankAccount?.status),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Created date',
        displayedValue: this.datePipe.transform(data?.syncedBankAccount?.createdDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Currency',
        displayedValue: data?.syncedBankAccount?.currency,
      }),
      HorizontalListFactory.createKeyValueRouterLinkItem({
        title: 'Customer ID',
        displayedValue: data?.syncedBankAccount?.customerId?.toString(),
        routerLink: this.getProviderCustomerLink(data),
        valueTooltipMessage: `${data?.syncedBankAccount?.providerName} customer`
      }),
      HorizontalListFactory.createKeyValueRouterLinkItem({
        title: 'Account ID',
        displayedValue: data?.syncedBankAccount?.id?.toString(),
        routerLink: this.getProviderAccountLink(data),
        valueTooltipMessage: `${data?.syncedBankAccount?.providerName} account`
      }),
    ]
  }

  private getProviderCustomerLink(data: BankAccount): string {
    return `/banking/${data?.syncedBankAccount?.providerName?.toLowerCase()}/customers/${data?.syncedBankAccount?.customerId}/accounts`;
  }

  private getProviderAccountLink(data: BankAccount): string {
    return `${this.getProviderCustomerLink(data)}/${data?.syncedBankAccount?.id}/transactions`;
  }

  adaptConfirmChangesData(form: UntypedFormGroup, bankAccount: BankAccount): ConfirmChangesDialogData {
    if (!form?.controls) {
      return undefined;
    }

    const actionType = bankAccount ? ConfirmChangeActionType.Update : ConfirmChangeActionType.Create;
    const actionGerund = `${actionType.substring(0, actionType.length - 1).toLowerCase()}ing`;
    const controls = form.controls;

    return {
      dialogTitle: `${actionType} bank account?`,
      heading: `You are ${actionGerund} a bank account for ${controls.businessName?.value}`,
      fields: [
        {
          label: "Legal entity name",
          value: controls.businessName?.value,
          isChanged: controls.businessName?.dirty,
        },
        {
          label: "Bank name",
          value: controls.bankName?.value,
          isChanged: controls.bankName?.dirty,
        },
        {
          label: "Bank account number",
          value: controls.accountNumber?.value,
          isChanged: controls.accountNumber?.dirty,
        },
        {
          label: "Bank routing number",
          value: controls.routingNumber?.value || 'Null',
          isChanged: controls.routingNumber?.dirty,
        },
        {
          label: "Bank account type",
          value: EnumHelper.getNameFromValue(BankAccountType, controls.bankAccountType?.value),
          isChanged: controls.bankAccountType?.dirty,
        },
        {
          label: "Bank designation (global)",
          value: this.getBankDesignationValue(controls.bankAccountDesignationEntity?.value),
          isChanged: controls.bankAccountDesignationEntity?.dirty,
        },
        {
          label: "Verification status",
          value: controls.verificationStatus?.value ? 'Verified' : 'Unverified',
          isChanged: controls.verificationStatus?.dirty,
        },
        {
          label: "Verification date",
          value: this.datePipe.transform(controls.lastCheckDate?.value) || 'Null',
          isChanged: controls.lastCheckDate?.dirty,
        },
        {
          label: "Screen share required",
          value: controls.screenShareRequired?.value ? 'True' : 'False',
          isChanged: actionType === ConfirmChangeActionType.Create || controls.screenShareRequired?.dirty,
        },
      ]
    }
  }

  private getBankDesignationValue(bankAccountDesignationEntity: string): string {
    const designationOptions = EnumHelper.getMappedNamesAndValues(BankAccountRole, BankAccountRoleLabel).filter(_ => _.name !== undefined);
    if (!bankAccountDesignationEntity) {
      return null;
    }
    return designationOptions.filter(option => option.value === bankAccountDesignationEntity)[0].name;
  }

  adaptDeleteDialogData(bankAccount: BankAccount): SimpleDialogData {
    const bankAccountNumber = BankAccountHelper.sanitizeBankAccountNumber(bankAccount?.accountNumber);
    const accountType = this.titleCasePipe.transform(bankAccount.accountType) ?? '';
    return {
      title: "Confirm delete?",
      body: `You are deleting ${bankAccount.bankName} - ${accountType} ${bankAccountNumber}`,
      actions: [
        { label: "Cancel", buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel},
        { label: "Confirm", buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm, initialFocus: true },
      ]
    }
  }

  adaptCannotDeleteDialogData(bankAccount: BankAccount): SimpleDialogData {
    const bankName = bankAccount?.bankName ? bankAccount.bankName : 'Your bank account on file';
    const bankAccountNumber = BankAccountHelper.sanitizeBankAccountNumber(bankAccount?.accountNumber);
    const role = this.getBankAccountRole(bankAccount.entityRole);
    const accountType = this.titleCasePipe.transform(bankAccount.accountType) ?? '';
    const bankData = `${bankName} - ${accountType} ${bankAccountNumber}`
    return {
      title: `Action cannot be done`,
      body: `${bankData} is a ${role}.<br>To delete ${bankData}, set a new ${role} bank.`
    }
  }

  private getBankAccountRole(entityRole: BankAccountRole): string {
    switch (entityRole) {
      case BankAccountRole.Payment:
        return "payment";
      case BankAccountRole.Disbursement:
        return "disbursement";
      case BankAccountRole.PaymentAndDisbursement:
        return "payment and disbursement";
      default:
        return "transfer";
    }
  }

  adaptSyncSuccessDialogData(bankAccount: BankAccount): SimpleDialogData {
    const accountType = this.titleCasePipe.transform(bankAccount.accountType) ?? '';
    return {
      title: "Sync successful",
      body: `Retrieving data from ${bankAccount.bankName} - ${accountType}`,
    }
  }

  adaptSyncErrorDialogData(bankAccount: BankAccount, error: any): SimpleDialogData {
    const accountType = this.titleCasePipe.transform(bankAccount.accountType) ?? '';
    const errorData = error?.error?.error ?? '';
    const errorDescription = error?.error?.error_description?? '';
    return {
      title: "Sync unsuccessful",
      body: `Unable to retrieve data from ${bankAccount.bankName} - ${accountType}<br>${errorData}<br>${errorDescription}`,
    }
  }
}